import { useState, useEffect } from 'react';
import { Dimensions, Platform } from 'react-native';

interface ResponsiveBreakpoints {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  isXLarge: boolean;
  isXXLarge: boolean;
  width: number;
  height: number;
  // New enhanced properties
  isTouch: boolean;
  isWeb: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  layoutMode: 'mobile' | 'tablet' | 'desktop';
  showSidebar: boolean;
  showFullTable: boolean;
  columnsToShow: 'minimal' | 'partial' | 'full';
}

// Enhanced breakpoints for better desktop support
const BREAKPOINTS = {
  sm: 640,   // Small devices (landscape phones, 640px and up)
  md: 768,   // Medium devices (tablets, 768px and up)
  lg: 1024,  // Large devices (desktops, 1024px and up)
  xl: 1280,  // Extra large devices (large desktops, 1280px and up)
  xxl: 1536, // Extra extra large devices (very large desktops, 1536px and up)
};

export const useResponsive = (): ResponsiveBreakpoints => {
  const [dimensions, setDimensions] = useState(() => {
    const { width, height } = Dimensions.get('window');
    return { width, height };
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      // On web, ignore height-only changes (mobile browser UI show/hide) to prevent render thrash
      setDimensions((prev) => {
        const nextWidth = window.width;
        const nextHeight = window.height;
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        if (Platform.OS === 'web' && prev.width === nextWidth) {
          return prev; // ignore height-only changes on web
        }
        return { width: nextWidth, height: nextHeight };
      });
    });

    return () => subscription?.remove();
  }, []);

  const { width, height } = dimensions;
  const isWeb = Platform.OS === 'web';

  // Determine device type based on width and platform
  const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
    if (!isWeb) {
      return width >= BREAKPOINTS.md ? 'tablet' : 'mobile';
    }
    if (width >= BREAKPOINTS.lg) return 'desktop';
    if (width >= BREAKPOINTS.md) return 'tablet';
    return 'mobile';
  };

  const deviceType = getDeviceType();

  // Determine layout mode (can be different from device type for flexibility)
  const getLayoutMode = (): 'mobile' | 'tablet' | 'desktop' => {
    // On web, use desktop layout for large screens
    if (isWeb && width >= BREAKPOINTS.lg) return 'desktop';
    // Use tablet layout for medium screens on web
    if (isWeb && width >= BREAKPOINTS.md) return 'tablet';
    // Use mobile layout for everything else
    return 'mobile';
  };

  const layoutMode = getLayoutMode();

  // Determine columns to show based on screen size
  const getColumnsToShow = (): 'minimal' | 'partial' | 'full' => {
    if (width >= BREAKPOINTS.xl) return 'full';      // Show all columns on XL+ screens
    if (width >= BREAKPOINTS.lg) return 'partial';   // Show most columns on large screens
    return 'minimal';                                 // Show minimal columns on smaller screens
  };

  // Base responsive properties
  const baseProps = {
    width,
    height,
    isWeb,
    isTouch: !isWeb, // Assume touch for native platforms
    deviceType,
    layoutMode,
    
    // Traditional breakpoint flags
    isSmall: width < BREAKPOINTS.sm,
    isMedium: width >= BREAKPOINTS.sm && width < BREAKPOINTS.md,
    isLarge: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isXLarge: width >= BREAKPOINTS.lg && width < BREAKPOINTS.xl,
    isXXLarge: width >= BREAKPOINTS.xxl,
    
    // Device type flags
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    
    // Layout behavior flags
    showSidebar: layoutMode === 'desktop',
    showFullTable: width >= BREAKPOINTS.lg,
    columnsToShow: getColumnsToShow(),
  };

  return baseProps;
};